import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useCreatives } from '@/hooks/useCreatives';
import { useAssets } from '@/hooks/useAssets';
import { formatRelativeDate } from '@/utils/date';
import { Icon } from '@/components/primitives/Icon';
import { Button } from '@/components/primitives/Button';
import { Badge } from '@/components/primitives/Badge';
import { Skeleton } from '@/components/primitives/Skeleton';
import { NewCreativeModal } from '@/components/modals/NewCreativeModal';

function SkeletonRow() {
  return (
    <div className="flex items-center justify-between p-5 bg-surface-container-lowest rounded-2xl border border-outline-variant/10">
      <div className="flex items-center gap-4">
        <Skeleton rounded="xl" className="w-11 h-11" />
        <div className="space-y-2">
          <Skeleton rounded="sm" className="h-4 w-48" />
          <Skeleton rounded="sm" className="h-3 w-28" />
        </div>
      </div>
      <Skeleton rounded="full" className="h-6 w-20" />
    </div>
  );
}

export default function DashboardPage() {
  const [isNewCreativeOpen, setIsNewCreativeOpen] = useState(false);
  const navigate = useNavigate();
  const campaigns = useCampaigns(0, 5);
  const creatives = useCreatives({ limit: 50 });
  const assets = useAssets(0, 1); // just need the count

  const campaignCount = campaigns.data?.length ?? 0;
  const creativeCount = creatives.data?.length ?? 0;
  const assetCountText = assets.loading ? '…' : assets.data != null ? '—' : '—';

  const KPI_CARDS = [
    {
      icon: 'auto_awesome',
      label: 'Active Creatives',
      value: creatives.loading ? '…' : String(creativeCount),
      delta: 'Total creatives',
    },
    {
      icon: 'campaign',
      label: 'Total Campaigns',
      value: campaigns.loading ? '…' : String(campaignCount),
      delta: 'In your workspace',
    },
    {
      icon: 'folder',
      label: 'Asset Library',
      value: assetCountText,
      delta: 'Uploaded assets',
    },
  ];

  return (
    <div className="p-4 md:p-12 w-full space-y-10">
      {/* Page header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-primary mb-1">
            Overview
          </p>
          <h1 className="text-4xl font-headline font-black tracking-tight text-on-surface">
            Dashboard
          </h1>
          <p className="text-on-surface-variant mt-1">
            Welcome back. Here&apos;s what&apos;s happening with your campaigns.
          </p>
        </div>
        <Button onClick={() => setIsNewCreativeOpen(true)} size="lg">
          <Icon name="add_circle" />
          New Creative
        </Button>
      </div>

      {/* KPI Cards */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {KPI_CARDS.map((card) => (
          <div
            key={card.label}
            className="bg-surface-container-lowest p-6 rounded-2xl border border-outline-variant/10 ambient-shadow flex flex-col justify-between"
          >
            <div className="flex items-start justify-between">
              <div className="p-2 bg-primary-container text-primary rounded-xl flex items-center justify-center">
                <Icon name={card.icon} />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-xs text-on-surface-variant font-black uppercase tracking-wider">
                {card.label}
              </p>
              <p className="text-4xl font-headline font-black text-on-surface mt-1">{card.value}</p>
              <p className="text-xs text-on-surface-variant mt-1">{card.delta}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Recent Campaigns */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-headline font-bold text-on-surface">Recent Campaigns</h2>
          <Link
            to="/campaigns"
            className="text-primary text-xs font-black uppercase tracking-wider hover:underline"
          >
            View All
          </Link>
        </div>

        {campaigns.error && (
          <div className="p-4 rounded-xl bg-error-container text-on-error-container text-sm">
            {campaigns.error}
          </div>
        )}

        <div className="space-y-3">
          {campaigns.loading && [1, 2, 3].map((i) => <SkeletonRow key={i} />)}

          {!campaigns.loading &&
            (campaigns.data ?? []).map((c) => {
              const status: 'Active' | 'Draft' | 'Archived' = 'Active';
              return (
                <Link
                  key={c.id}
                  to={`/campaigns/${c.id}`}
                  className="group flex items-center justify-between p-5 bg-surface-container-lowest rounded-2xl border border-outline-variant/10 hover:border-primary/20 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary-container flex items-center justify-center">
                      <Icon name="campaign" className="text-primary" />
                    </div>
                    <div>
                      <p className="font-bold text-on-surface group-hover:text-primary transition-colors font-headline">
                        {c.name}
                      </p>
                      <p className="text-xs text-on-surface-variant mt-0.5">
                        Updated {formatRelativeDate(c.updated_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <Badge status={status} />
                    <Icon
                      name="chevron_right"
                      className="text-on-surface-variant group-hover:text-primary transition-colors"
                    />
                  </div>
                </Link>
              );
            })}

          {!campaigns.loading && campaigns.data?.length === 0 && (
            <div className="py-16 text-center">
              <Icon name="campaign" className="text-5xl text-primary/30" />
              <p className="mt-4 text-on-surface-variant font-medium">No campaigns yet.</p>
              <Link
                to="/campaigns"
                className="mt-4 inline-flex items-center gap-2 text-primary font-bold text-sm hover:underline"
              >
                Create your first campaign
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Recent Creatives */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-headline font-bold text-on-surface">Recent Creatives</h2>
          <Link
            to="/creatives"
            className="text-primary text-xs font-black uppercase tracking-wider hover:underline"
          >
            View All
          </Link>
        </div>

        {creatives.error && (
          <div className="p-4 rounded-xl bg-error-container text-on-error-container text-sm">
            {creatives.error}
          </div>
        )}

        <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/10 overflow-hidden">
          <div className="grid grid-cols-[1fr_160px_100px] gap-4 px-6 py-3 bg-surface-container-low/60 border-b border-outline-variant/10">
            {['Creative', 'Template', 'Updated'].map((h) => (
              <p
                key={h}
                className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant/60"
              >
                {h}
              </p>
            ))}
          </div>

          {creatives.loading && (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} rounded="lg" className="h-10" />
              ))}
            </div>
          )}

          {!creatives.loading &&
            (creatives.data ?? []).slice(0, 5).map((cr) => (
              <Link
                key={cr.id}
                to={`/creatives/${cr.id}`}
                className="grid grid-cols-[1fr_160px_100px] gap-4 items-center px-6 py-4 border-b border-outline-variant/10 last:border-0 hover:bg-primary-container/20 transition-colors cursor-pointer"
              >
                <p className="font-bold text-sm text-on-surface truncate">{cr.name}</p>
                <p className="text-sm text-on-surface-variant truncate">{cr.template_id}</p>
                <p className="text-xs text-on-surface-variant">
                  {formatRelativeDate(cr.updated_at)}
                </p>
              </Link>
            ))}

          {!creatives.loading && creatives.data?.length === 0 && (
            <div className="py-10 text-center">
              <p className="text-on-surface-variant text-sm">No creatives yet.</p>
            </div>
          )}
        </div>
      </section>

      <NewCreativeModal
        isOpen={isNewCreativeOpen}
        onClose={() => setIsNewCreativeOpen(false)}
        onCreated={(created) => navigate(`/creatives/${created.id}`)}
      />
    </div>
  );
}
